import { LegalPage } from "../components/LegalPage";
import { CONTACT_EMAIL } from "../lib/contact";

/** Privacy Policy (`/privacy`). See `LegalPage` for shared chrome. */
export function Privacy() {
  return (
    <LegalPage title="Privacy Policy">
      <p>
        This Privacy Policy explains how FaB AI Judge (the "Service") collects,
        uses, stores, and shares information. It applies to the Service and does
        not govern third-party websites or services that have their own privacy
        policies.
      </p>
      <p>
        The Service is an experimental, unofficial AI-powered rules assistant
        for <em>Flesh and Blood</em>. Standalone first questions and their
        answers may be stored in a shared answer cache and shown to other users,
        and conversations you share through a link are publicly accessible. You
        should not submit personal, confidential, or sensitive information.
      </p>

      <h2>1. Who operates the Service</h2>
      <p>
        FaB AI Judge is operated by an independent individual ("we," "us," or
        "our").
      </p>
      <p>For privacy-related questions or requests, contact:</p>
      <p>
        <strong>Email:</strong>{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>
      <p>
        We have not designated a separate privacy representative. Whether
        additional representative or registration requirements apply depends on
        the laws applicable to the Service and its processing activities.
      </p>

      <h2>2. Information we collect</h2>
      <p>
        Depending on how you use the Service, the following categories of
        information may be collected or processed.
      </p>

      <h3>2.1 Account and sign-in information</h3>
      <p>
        When you sign in using Google, the Service may receive account
        information made available through the sign-in integration, such as your
        email address, name, profile picture, Google account identifier, and
        authentication-related information.
      </p>
      <p>
        We do not receive your Google password through the sign-in integration.
        Authentication sessions and related information may be stored in your
        browser or processed by our authentication provider.
      </p>

      <h3>2.2 Questions, answers, and conversation history</h3>
      <p>
        We store or process the questions you submit, AI-generated answers,
        follow-up questions and answers, and related information, such as cited
        card data, rules references, timestamps, or other conversation metadata.
      </p>
      <p>
        <strong>
          Standalone first questions and their answers may be stored in the
          shared answer cache and shown to other users, without attribution to
          your account.
        </strong>{" "}
        Follow-up questions and answers are not cached. Conversations are
        visible to you, and to anyone you share them with through a public share
        link. Do not include information in a question that you would not want
        another user to see.
      </p>
      <p>
        You can delete individual conversations from your history. Deleting a
        conversation removes the conversation and its messages from your
        account. It does not remove the corresponding entry from the shared
        answer cache (see §2.3 and §5), and it does not recall content that
        other people have already copied, indexed, or accessed through a public
        link.
      </p>

      <h3>2.3 Shared answer cache</h3>
      <p>
        The Service maintains a shared cache of questions and answers to reuse
        previous results and reduce processing costs and latency. Depending on
        the implementation, cached records may include question text, generated
        answers, numeric embeddings derived from questions, and related
        metadata.
      </p>
      <p>
        The cache is shared across users. Cached content may be used to answer
        similar questions and may be reviewed for quality or service operation.
        Cached questions and answers are retained permanently under the
        Service's current policy. There is no user-facing feature to delete an
        individual cached entry.
      </p>
      <p>
        Cache entries are not linked to your account and are not deleted when
        you delete a conversation from your history. Only standalone first-turn
        questions are cached; follow-up questions and answers are not. Deleting
        a conversation therefore removes the conversation but leaves any cached
        first question and its answer in the shared cache.
      </p>

      <h3>2.4 Feedback and service-quality information</h3>
      <p>
        If the Service offers feedback controls, we collect the rating you
        submit (for example, a thumbs up or down on an answer) and related
        review status to assess answer quality and investigate issues.
      </p>

      <h3>2.5 Usage and operational records</h3>
      <p>
        The Service and its providers may process information such as account
        identifiers, sign-in events, timestamps, usage records, request
        metadata, IP addresses, error logs, and security-related events. The
        precise information collected depends on the relevant provider and the
        Service's configuration.
      </p>
      <h3>2.6 Voluntary support</h3>
      <p>
        The Service does not process payments. Optional support is handled
        entirely by Ko-fi, which processes your payment under its own terms and
        privacy policy.
      </p>
      <p>
        If you support the project through Ko-fi, we receive and store the
        details Ko-fi sends us, which may include your name, email address,
        message, amount, currency, and transaction identifiers. We use this
        information only to keep a record of support and to reply to you.
      </p>

      <h3>2.8 Browser storage</h3>
      <p>
        The Service uses browser storage and authentication-session mechanisms
        to support sign-in and may store information such as recent conversation
        history or cached data locally to improve performance.
      </p>
      <p>
        The Service does not use advertising cookies. Authentication or
        infrastructure providers may use their own storage mechanisms as part of
        providing their services.
      </p>

      <h2>3. How we use information</h2>
      <p>We may use the information described above to:</p>
      <ul>
        <li>Provide and operate the Service.</li>
        <li>Authenticate users and maintain account sessions.</li>
        <li>
          Receive questions, generate answers, and provide conversation history.
        </li>
        <li>Maintain and use the shared answer cache.</li>
        <li>Operate, troubleshoot, maintain, and secure the Service.</li>
        <li>Review feedback and assess answer quality.</li>
        <li>Enforce usage limits and the Terms of Service.</li>
        <li>
          Detect, investigate, and address abuse, fraud, security incidents, and
          technical problems.
        </li>
        <li>
          Comply with applicable legal obligations and respond to lawful
          requests.
        </li>
        <li>Keep a record of voluntary support and respond to you about it.</li>
        <li>Send service-related notices where necessary.</li>
      </ul>
      <p>
        We do not sell personal information or use it for third-party
        advertising.
      </p>

      <h2>4. Public content and shared conversation links</h2>
      <p>
        Standalone first questions and their answers may enter the shared answer
        cache and be shown to other users, without attribution to your account.
        In addition, any conversation you share through a public share link is
        accessible to anyone who obtains the link, including its follow-up
        exchanges.
      </p>
      <p>
        Anyone who can access publicly exposed content or a shared link may be
        able to read the relevant conversation. Content may be copied, indexed,
        or retained by third parties outside our control. Removing content from
        one location would not necessarily remove copies held by other users,
        search engines, or third-party services.
      </p>
      <p>
        If you delete a conversation, it is removed from your history and any
        share link for it stops working. This does not remove copies that other
        users, search engines, or third-party services may already hold, and it
        does not remove a corresponding entry from the shared answer cache.
      </p>
      <p>
        Do not include names, contact details, account credentials, private
        game-group information, confidential material, or other information you
        do not want disclosed.
      </p>

      <h2>5. The shared answer cache and retention</h2>
      <p>
        The shared cache is used to reuse answers to similar questions. It may
        contain question text, answers, embeddings, and related metadata.
      </p>
      <p>
        Cached questions and answers are retained permanently under the
        Service's current policy. There is no user-facing mechanism to remove an
        individual entry from the cache. A request concerning account data does
        not necessarily remove a corresponding cached entry, copies already
        disclosed publicly, or copies retained by third parties.
      </p>
      <p>
        You can delete individual conversations from your history, which deletes
        the conversation and its messages. Conversation records and other
        account-associated information are otherwise retained while the account
        is active, subject to the Service's operational needs and applicable
        legal requirements. Account deletion is not currently implemented as a
        self-service feature. Requests for account-data deletion can be
        submitted by email and will be handled manually, subject to applicable
        law and the Service's technical capabilities.
      </p>
      <p>
        Operational and server logs are retained for a limited period, but the
        exact period may depend on the systems and providers involved. We do not
        specify a fixed duration here because the applicable retention settings
        have not been confirmed.
      </p>
      <p>
        Some information may need to be retained where required by law or where
        otherwise permitted or required to establish, exercise, or defend legal
        claims. The scope and duration of any such retention depend on the
        circumstances and applicable law.
      </p>

      <h2>6. AI processing and third-party service providers</h2>
      <p>
        The Service uses third-party providers to operate its infrastructure,
        authenticate users, and generate AI answers. The providers currently
        identified for the Service are:
      </p>
      <ul>
        <li>
          <strong>Supabase:</strong> Authentication, database, and related
          backend services.
        </li>
        <li>
          <strong>Cloudflare:</strong> Hosting, delivery, and/or infrastructure
          services.
        </li>
        <li>
          <strong>Google:</strong> Sign-in and related authentication services.
        </li>
        <li>
          <strong>OpenAI:</strong> The AI API provider that processes questions
          and generates responses. The provider or model may change.
        </li>
        <li>
          <strong>Ko-fi:</strong> Receives optional voluntary support and shares
          the support details recorded above with the Service.
        </li>
      </ul>
      <p>
        The exact services used and the configuration of each provider may
        change.
      </p>
      <p>
        When you submit a question, the question and potentially related context
        may be sent to the AI API provider for processing. Depending on the
        implementation, this may include follow-up messages or other
        conversation context necessary to generate an answer.
      </p>
      <p>
        The AI provider's data-retention, logging, and model-training practices
        have not been fully verified for the configuration used by the Service.
        The provider may retain prompts or use submitted data for training,
        depending on its terms, settings, and applicable policies.{" "}
        <strong>
          We therefore do not promise that information sent to the AI provider
          is never retained or never used for model training.
        </strong>
      </p>
      <p>
        Each provider may process information under its own contractual terms
        and privacy policies. Depending on the relationship and applicable law,
        a provider may process data on our behalf or independently for its own
        purposes.
      </p>
      <p>
        We do not control third-party services in all respects, and their
        practices may change. Consult the providers' current terms and privacy
        documentation for further details.
      </p>

      <h2>7. How information is shared</h2>
      <p>
        Information may be disclosed or made accessible in the following
        circumstances:
      </p>
      <ul>
        <li>
          <strong>Public access:</strong> Cached first questions and answers may
          be shown to other users, and conversations you share through a public
          link are accessible to anyone with the link.
        </li>
        <li>
          <strong>Service providers:</strong> Information may be processed by
          Supabase, Cloudflare, Google, OpenAI (the AI API provider), Ko-fi, and
          other infrastructure providers used to operate the Service.
        </li>
        <li>
          <strong>Legal obligations:</strong> We may disclose information when
          required by applicable law or a valid legal process.
        </li>
        <li>
          <strong>Safety and enforcement:</strong> We may disclose information
          where reasonably necessary and legally permitted to investigate abuse,
          enforce the Terms of Service, or protect the rights, property, safety,
          or security of the Service, users, or others.
        </li>
        <li>
          <strong>Business or operational transfer:</strong> If the Service or
          its relevant assets are transferred, information may be transferred as
          part of that transaction, subject to applicable law.
        </li>
      </ul>
      <p>
        We do not sell personal information or share it for third-party
        advertising.
      </p>

      <h2>8. International data transfers</h2>
      <p>
        The Service is intended for an international audience and is not
        intentionally targeted at the European Union. This does not necessarily
        mean that EU, UK, or other foreign privacy laws cannot apply.
      </p>
      <p>
        Our providers may process information in countries other than the one
        where you live. The relevant locations and transfer arrangements depend
        on provider services and configuration.
      </p>
      <p>
        We do not make a blanket promise that a particular transfer mechanism or
        safeguard is used for every transfer. Where applicable law requires
        specific safeguards or transfer arrangements, those requirements must be
        assessed and implemented for the relevant processing.
      </p>

      <h2>9. Your choices and privacy requests</h2>
      <p>
        You can choose not to submit information to the Service. However, some
        features may require sign-in or processing of your questions by
        third-party providers.
      </p>
      <p>
        You may contact us at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> to ask about
        your personal information or request access, correction, deletion, or
        another action available under applicable law.
      </p>
      <p>
        Account deletion is not currently available through a self-service
        feature. Requests are handled manually. We will assess requests under
        the applicable law and the technical capabilities of the Service.
      </p>
      <p>
        You can delete individual conversations from your history. This removes
        the conversation and its messages, but it does not remove any entry in
        the shared answer cache, content already made public, or copies held by
        third parties.
      </p>
      <p>Please be aware of the following limitations:</p>
      <ul>
        <li>
          There is no user-facing feature to delete individual shared-cache
          entries.
        </li>
        <li>
          Account-data deletion does not automatically guarantee deletion of
          cached content, content already made public, copies held by third
          parties, or records that must or may lawfully be retained.
        </li>
        <li>
          Publicly accessible content may have been copied, indexed, or
          preserved by others.
        </li>
        <li>
          The availability and scope of privacy rights depend on the law that
          applies to the particular processing and request.
        </li>
      </ul>
      <p>
        Where applicable law grants you specific rights, this section does not
        waive those rights. You may also have the right to lodge a complaint
        with a relevant data protection authority.
      </p>

      <h2>10. Security</h2>
      <p>
        We and our service providers use technical and organizational measures
        intended to protect information. The measures in place depend on the
        systems and providers involved.
      </p>
      <p>
        No method of transmission, storage, or processing is completely secure,
        and we cannot guarantee absolute security. You should not submit
        information that needs to remain confidential.
      </p>
      <p>
        Because questions and answers are treated as public,{" "}
        <strong>
          do not use the Service to store secrets or private information
        </strong>
        , even if a particular page or link appears difficult to discover.
      </p>

      <h2>11. Age requirement</h2>
      <p>
        The Service is intended for adults only. You must be at least 18 years
        old to use it, and we do not knowingly collect personal information from
        anyone under 18.
      </p>
      <p>
        If you believe someone under 18 has submitted personal information,
        contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        We will review the request and take action as required by applicable law
        and the Service's technical capabilities. We do not promise deletion
        from the permanent shared cache where the Service has no mechanism to
        remove individual cached entries.
      </p>

      <h2>12. Third-party websites and services</h2>
      <p>
        The Service may link to third-party websites, card databases, rules
        resources, or provider documentation. Those services are governed by
        their own terms and privacy policies. We are not responsible for their
        privacy practices or content.
      </p>

      <h2>13. Changes to this Privacy Policy</h2>
      <p>
        We may revise this Privacy Policy as the Service, its providers, or
        applicable legal requirements change. The updated version will be
        published at <a href="/privacy">/privacy</a> with a revised "Last
        updated" date.
      </p>
      <p>
        The updated policy applies from the date indicated in the published
        version, subject to any notice or consent requirements imposed by
        applicable law. Continued use of the Service does not override rights
        that cannot legally be waived or any notice or consent requirement that
        applies.
      </p>

      <h2>14. Contact</h2>
      <p>For privacy-related questions or requests, contact:</p>
      <p>
        <strong>FaB AI Judge operator</strong>
        <br />
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>
    </LegalPage>
  );
}
